import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Card, CardContent, Divider, LinearProgress, Skeleton, Stack, Typography, useTheme, } from "@mui/material";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { MONITORING_RENDER_WIDGET } from "@/queries/MonitoringRenderWidget";
import { DEFAULT_WIDGET_HEIGHT, EColorScheme, EMonitoringCapability, EMonitoringExportFormat, EProducerState, EWidgetFailure, EWidgetRenderState, } from "./types";
import { resolveSelectors, selectorValuesForRequest } from "./lib/selectors";
import { CHART_FONT_CSS } from "./lib/chartFonts";
import { BUSY_RETRY_MS, EMonitoringErrorCode, monitoringErrorCode, monitoringErrorMessage, } from "./lib/errors";
import { useMonitoring } from "./MonitoringProvider";
import { useBucketedWidth } from "./useBucketedWidth";
import { MonitoringChartFrame } from "./MonitoringChartFrame";
import { MonitoringWidgetUnavailable } from "./MonitoringWidgetUnavailable";
import { MonitoringWidgetSelectors } from "./MonitoringWidgetSelectors";
import { MonitoringWidgetMenu } from "./MonitoringWidgetMenu";
import { MonitoringDataTableDialog } from "./MonitoringDataTableDialog";
import { widgetQueries } from "./lib/dataTables";
import { noticeText } from "./lib/notices";
import { MonitoringExportDialog } from "./MonitoringExportDialog";
var EWidgetDialog;
(function (EWidgetDialog) {
    EWidgetDialog["NONE"] = "NONE";
    EWidgetDialog["DATA"] = "DATA";
    EWidgetDialog["EXPORT"] = "EXPORT";
})(EWidgetDialog || (EWidgetDialog = {}));
export function MonitoringWidgetCard(_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j, _k;
    var cell = _a.cell, context = _a.context;
    var _l = useTranslation(), t = _l.t, i18n = _l.i18n;
    var theme = useTheme();
    var _m = useMonitoring(), widgetValues = _m.widgetValues, setWidgetValue = _m.setWidgetValue, actions = _m.actions;
    var _o = useState(EWidgetDialog.NONE), dialog = _o[0], setDialog = _o[1];
    var body = useRef(null);
    var width = useBucketedWidth(body);
    var widget = cell.widget;
    var picks = widgetValues(cell);
    var eventDays = context.eventDays;
    var selectors = useMemo(function () { return (widget ? resolveSelectors(widget, cell.values, picks, { event_days: eventDays }) : []); }, [widget, cell.values, picks, eventDays]);
    var selectorValues = useMemo(function () { return selectorValuesForRequest(selectors); }, [selectors]);
    var source = widget ? context.sources[widget.source] : undefined;
    var notConnected = (source === null || source === void 0 ? void 0 : source.producer) === EProducerState.NOT_CONNECTED;
    var colorScheme = theme.palette.mode === "dark" ? EColorScheme.DARK : EColorScheme.LIGHT;
    var _p = useQuery(MONITORING_RENDER_WIDGET, {
        variables: {
            electionEventId: context.electionEventId,
            electionId: (_b = context.electionId) !== null && _b !== void 0 ? _b : null,
            dashboardId: context.dashboardId,
            widgetId: cell.widgetId,
            scope: context.scope,
            selectorValues: selectorValues,
            snapshotRevision: (_d = (_c = context.snapshot) === null || _c === void 0 ? void 0 : _c.revision) !== null && _d !== void 0 ? _d : null,
            width: width !== null && width !== void 0 ? width : 0,
            colorScheme: colorScheme,
            locale: i18n.language,
        },
        // Polls do not draw a widget again unless the snapshot, scope or
        // selectors changed, since the request stays the same. Renders are not
        // cached: each snapshot revision would leave its SVGs in the cache.
        fetchPolicy: "no-cache",
        skip: !widget || notConnected || width === null,
    }), data = _p.data, previousData = _p.previousData, loading = _p.loading, error = _p.error, refetch = _p.refetch;
    // A saved change to the widget, dashboard, theme or settings keeps the
    // request the same, so it is asked again explicitly.
    var version = "".concat((_e = cell.revision) !== null && _e !== void 0 ? _e : "", "/").concat(context.configVersion);
    var drawnVersion = useRef(version);
    useEffect(function () {
        if (drawnVersion.current === version)
            return;
        drawnVersion.current = version;
        void refetch().catch(function () { return undefined; });
    }, [version, refetch]);
    // A failed widget asks again on the dashboard's next poll; a busy one sooner.
    var errorCode = error ? monitoringErrorCode(error) : undefined;
    var polled = useRef(context.pollCount);
    useEffect(function () {
        if (polled.current === context.pollCount)
            return;
        polled.current = context.pollCount;
        if (error)
            void refetch().catch(function () { return undefined; });
    }, [context.pollCount, error, refetch]);
    var busy = errorCode === EMonitoringErrorCode.BUSY ? error : undefined;
    useEffect(function () {
        // Each busy answer waits, then asks again.
        if (!busy)
            return;
        var timer = window.setTimeout(function () { return void refetch().catch(function () { return undefined; }); }, BUSY_RETRY_MS);
        return function () { return window.clearTimeout(timer); };
    }, [busy, refetch]);
    // The previous chart only while the next one is on its way, marked as such;
    // after a failure, the failure.
    var updating = loading && !data && Boolean(previousData);
    var render = error
        ? undefined
        : (_f = (data !== null && data !== void 0 ? data : (loading ? previousData : undefined))) === null || _f === void 0 ? void 0 : _f.monitoringRenderWidget;
    var hasData = Boolean(((_g = render === null || render === void 0 ? void 0 : render.tables) === null || _g === void 0 ? void 0 : _g.some(function (query) { return query.table; })) || (render === null || render === void 0 ? void 0 : render.table));
    var title = (_h = widget === null || widget === void 0 ? void 0 : widget.title) !== null && _h !== void 0 ? _h : cell.widgetId;
    var height = (_j = widget === null || widget === void 0 ? void 0 : widget.height) !== null && _j !== void 0 ? _j : DEFAULT_WIDGET_HEIGHT;
    var canConfigure = context.configure === EMonitoringCapability.GRANTED;
    var content = function () {
        if (!widget) {
            return (_jsx(MonitoringWidgetUnavailable, { state: EWidgetRenderState.INVALID, problem: cell.problem === "missing"
                    ? t("monitoring.widget.missing", { id: cell.widgetId })
                    : cell.problem, title: title }));
        }
        if (notConnected) {
            return (_jsx(MonitoringWidgetUnavailable, { state: EWidgetRenderState.NOT_CONNECTED, reason: source === null || source === void 0 ? void 0 : source.reason, title: title }));
        }
        if (error) {
            return (_jsx(MonitoringWidgetUnavailable, { state: EWidgetFailure.REQUEST_FAILED, problem: errorCode ? t(monitoringErrorMessage(error)) : undefined, title: title }));
        }
        if (!render) {
            return (_jsx(Skeleton, { variant: "rectangular", height: height, role: "progressbar", "aria-label": t("monitoring.widget.loading", { widget: title }) }));
        }
        if (render.state === EWidgetRenderState.RENDERED && render.svg) {
            return (_jsx(MonitoringChartFrame, { svg: render.svg, title: t("monitoring.frame.title", { widget: title }), height: height, colorScheme: colorScheme, fontCss: CHART_FONT_CSS }));
        }
        return (_jsx(MonitoringWidgetUnavailable, { state: render.state === EWidgetRenderState.RENDERED
                ? EWidgetRenderState.RENDER_FAILED
                : render.state, reason: render.reason, diagnostics: render.diagnostics, table: render.table, title: title }));
    };
    return (_jsxs(Card, { variant: "outlined", sx: { height: "100%" }, "data-widget-id": cell.widgetId, children: [_jsx(CardContent, { children: _jsxs(Stack, { spacing: 1, children: [_jsxs(Stack, { direction: "row", alignItems: "center", spacing: 1, children: [_jsx(Typography, { variant: "subtitle1", component: "h3", sx: { flexGrow: 1, minWidth: 0, fontWeight: 600, color: "brandColor" }, children: title }), _jsx(MonitoringWidgetMenu, { widgetTitle: title, onConfigure: canConfigure && actions.onConfigureWidget
                                        ? function () {
                                            var _a;
                                            return (_a = actions.onConfigureWidget) === null || _a === void 0 ? void 0 : _a.call(actions, cell.widgetId, width !== null && width !== void 0 ? width : undefined);
                                        }
                                        : undefined, onViewData: hasData ? function () { return setDialog(EWidgetDialog.DATA); } : undefined, onExport: widget && context.snapshot
                                        ? function () { return setDialog(EWidgetDialog.EXPORT); }
                                        : undefined, onDuplicate: canConfigure && actions.onDuplicateWidget
                                        ? function () { var _a; return (_a = actions.onDuplicateWidget) === null || _a === void 0 ? void 0 : _a.call(actions, cell.widgetId); }
                                        : undefined })] }), _jsx(Divider, {}), (widget === null || widget === void 0 ? void 0 : widget.description) ? (_jsx(Typography, { variant: "caption", color: "text.secondary", children: widget.description })) : null, _jsx(MonitoringWidgetSelectors, { widgetId: cell.key, selectors: selectors, onChange: function (name, value) { return setWidgetValue(cell, name, value); } }), _jsxs(Box, { ref: body, "aria-busy": updating || undefined, children: [updating ? (_jsxs(Stack, { spacing: 0.5, role: "status", sx: { mb: 1 }, children: [_jsx(LinearProgress, { "aria-label": t("monitoring.widget.updating", { widget: title }) }), _jsx(Typography, { variant: "caption", color: "text.secondary", children: t("monitoring.widget.updatingNote") })] })) : null, _jsx(Box, { sx: updating ? { opacity: 0.5 } : undefined, children: content() })] }), render === null || render === void 0 ? void 0 : render.notices.map(function (notice) { return (_jsx(Typography, { variant: "caption", color: "text.secondary", children: noticeText(t, notice) }, notice)); })] }) }), render && hasData ? (_jsx(MonitoringDataTableDialog, { open: dialog === EWidgetDialog.DATA, onClose: function () { return setDialog(EWidgetDialog.NONE); }, title: title, scope: context.scopeLabel, table: render.table, tables: render.tables, queries: widgetQueries(widget), notices: render.notices })) : null, context.snapshot ? (_jsx(MonitoringExportDialog, { open: dialog === EWidgetDialog.EXPORT, onClose: function () { return setDialog(EWidgetDialog.NONE); }, title: title, scope: context.scopeLabel, timeZone: context.timeZone, initialFormat: EMonitoringExportFormat.CSV, onSnapshotPruned: context.onSnapshotPruned, widgets: widget ? [widget] : [], target: {
                    electionEventId: context.electionEventId,
                    electionId: (_k = context.electionId) !== null && _k !== void 0 ? _k : null,
                    dashboardId: context.dashboardId,
                    widgetId: cell.widgetId,
                    scope: context.scope,
                    selectorValues: selectorValues,
                    snapshotRevision: context.snapshot.revision,
                } })) : null] }));
}
